import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, Clock, CheckCircle, XCircle, Brain, Lock } from 'lucide-react';
import api from '../../lib/api';

function UserDashboard() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [attempts, setAttempts] = useState({});
  const [psychometricAttempts, setPsychometricAttempts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await api.get('/tests');
      setTests(response.data);
      
      // Fetch attempts for each test
      const attemptsData = {};
      const psychoData = {};
      for (const test of response.data) {
        try {
          const attemptsRes = await api.get(`/attempts/test/${test.id}/my-attempts`);
          attemptsData[test.id] = attemptsRes.data;
        } catch (error) {
          attemptsData[test.id] = [];
        }
        
        // Fetch psychometric attempts if test has psychometric component
        if (test.has_psychometric_test) {
          try {
            const psychoRes = await api.get('/psychometric/my-attempts');
            const testPsychoAttempts = psychoRes.data.filter(a => a.test_id === test.id);
            psychoData[test.id] = testPsychoAttempts;
          } catch (error) {
            psychoData[test.id] = [];
          }
        }
      }
      setAttempts(attemptsData);
      setPsychometricAttempts(psychoData);
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTestStatus = (test) => {
    const testAttempts = attempts[test.id] || [];
    // Only count completed attempts (not terminated ones) for the attempt limit
    const completedAttempts = testAttempts.filter(a => a.status === 'completed');
    
    if (completedAttempts.length >= test.config.attempts_allowed) {
      return { status: 'completed', label: 'Maximum attempts reached', color: 'default' };
    } else if (testAttempts.some(a => a.status === 'in_progress')) {
      return { status: 'in_progress', label: 'In Progress', color: 'warning' };
    } else {
      return { status: 'available', label: 'Available', color: 'default' };
    }
  };

  const getPsychometricStatus = (test) => {
    const psychoAttempts = psychometricAttempts[test.id] || [];
    const completed = psychoAttempts.some(a => a.status === 'completed');
    const inProgress = psychoAttempts.some(a => a.status === 'in_progress');
    
    if (completed) return { status: 'completed', label: 'Completed' };
    if (inProgress) return { status: 'in_progress', label: 'In Progress' };
    return { status: 'available', label: 'Not Started' };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="user-dashboard-title">My Tests</h1>
          <p className="text-gray-600">View and take your assigned tests</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tests.map((test) => {
            const status = getTestStatus(test);
            const testAttempts = attempts[test.id] || [];
            const lastAttempt = testAttempts[testAttempts.length - 1];
            const psychoStatus = test.has_psychometric_test ? getPsychometricStatus(test) : null;
            const isLocked = test.is_locked === true;
            
            return (
              <Card key={test.id} className={`p-6 ${isLocked ? 'opacity-75 bg-gray-50' : ''}`} data-testid={`test-card-${test.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 ${isLocked ? 'bg-gray-200' : 'bg-blue-100'} rounded-lg flex items-center justify-center mr-4`}>
                      {isLocked ? (
                        <Lock className="w-6 h-6 text-gray-500" />
                      ) : (
                        <FileText className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{test.title}</h3>
                      {isLocked ? (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800">Locked</Badge>
                      ) : (
                        <Badge variant={status.color}>{status.label}</Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">{test.description}</p>
                
                {/* Locked Test Message */}
                {isLocked && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Test Locked</span>
                    </div>
                    <p className="text-xs text-amber-700 mt-1">
                      {test.locked_reason || 'Complete previous tests to unlock this one.'}
                    </p>
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {test.config.duration} mins
                  </div>
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    {test.question_ids?.length || 0} questions
                  </div>
                </div>
                
                {lastAttempt && lastAttempt.status === 'completed' && test.config?.show_results_immediately && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Last Score</p>
                    <div className="flex items-center">
                      {lastAttempt.percentage >= test.config.pass_percentage ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 mr-2" />
                      )}
                      <span className="font-bold text-lg">{lastAttempt.percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                )}
                
                {lastAttempt && lastAttempt.status === 'completed' && !test.config?.show_results_immediately && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Test submitted. Results will be shared by admin.
                    </p>
                  </div>
                )}
                
                {/* Psychometric Test Section */}
                {test.has_psychometric_test && (
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-800">Psychometric Assessment</span>
                      </div>
                      <Badge variant={psychoStatus.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                        {psychoStatus.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-purple-600 mb-2">60 questions • 60 minutes</p>
                    {psychoStatus.status !== 'completed' && (
                      <Button
                        size="sm"
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        onClick={() => navigate(`/psychometric/${test.id}`)}
                        data-testid={`start-psychometric-${test.id}`}
                      >
                        <Brain className="w-4 h-4 mr-1" />
                        {psychoStatus.status === 'in_progress' ? 'Resume Assessment' : 'Start Assessment'}
                      </Button>
                    )}
                    {psychoStatus.status === 'completed' && test.psychometric_results_visible && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
                        onClick={() => {
                          const latestAttempt = (psychometricAttempts[test.id] || []).find(a => a.status === 'completed');
                          if (latestAttempt) navigate(`/psychometric-results/${latestAttempt.id}`);
                        }}
                        data-testid={`view-psychometric-results-${test.id}`}
                      >
                        View Results
                      </Button>
                    )}
                  </div>
                )}
                
                <div className="flex gap-2">
                  {isLocked ? (
                    <Button
                      className="flex-1 bg-gray-400 cursor-not-allowed"
                      disabled
                      data-testid={`locked-test-${test.id}`}
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Locked
                    </Button>
                  ) : status.status === 'in_progress' ? (
                    <Button
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                      onClick={() => navigate(`/test/${test.id}`)}
                      data-testid={`resume-test-${test.id}`}
                    >
                      Resume Test
                    </Button>
                  ) : status.status !== 'completed' && (
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate(`/test/${test.id}`)}
                      data-testid={`start-test-${test.id}`}
                    >
                      Start Test
                    </Button>
                  )}
                  {testAttempts.length > 0 && test.config?.show_results_immediately && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/results/${testAttempts[testAttempts.length - 1].id}`)}
                      data-testid={`view-results-${test.id}`}
                    >
                      View Results
                    </Button>
                  )}
                </div>
                
                <div className="mt-4 text-xs text-gray-500">
                  Attempts: {testAttempts.filter(a => a.status === 'completed').length} / {test.config.attempts_allowed}
                </div>
              </Card>
            );
          })}
        </div>

        {tests.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tests assigned</h3>
            <p className="text-gray-500">Check back later for new assignments</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default UserDashboard;